import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ExtractedEvent } from '../types';
import {
  compareEventDates, eventDatePart, eventDayKey, eventTodayKey,
  formatRange, isAllDayEvent, isStaleRecurringProjection, timePillParts,
} from '../lib/eventDates';
import { addCalendarDays, addCalendarMonths, calendarWeekday } from '../lib/eventTemporal';
import { formatTag } from '../lib/tagTaxonomy';
import { useFavorites, isEventHighlighted, eventFavoriteReason } from '../lib/favorites';
import EventReviewWarningChips from './EventReviewWarningChips';
import { DateProofWarning, dateProofRangeOptions, nonTemporalWarnings, useDateProofEvents } from '../lib/dateProofReview';

interface Props {
  events: ExtractedEvent[];
  onSelectEvent: (event: ExtractedEvent) => void;
}

type CalView = 'month' | 'week';

// Color palette for categories (cycles if > 8 categories). Values reference
// the --mh-chart-* tokens in styles.css so light + dark themes stay in sync
// and the brand palette can be tuned in one place.
const CAT_PALETTE = [
  'var(--mh-chart-1)', 'var(--mh-chart-2)', 'var(--mh-chart-3)', 'var(--mh-chart-4)',
  'var(--mh-chart-5)', 'var(--mh-chart-6)', 'var(--mh-chart-7)', 'var(--mh-chart-8)',
];

function calendarLabel(date: string, options: Parameters<typeof eventDatePart>[1]): string {
  return eventDatePart({ startUtc: date, isAllDay: true }, options);
}

export default function CalendarView({ events: loadedEvents, onSelectEvent }: Props) {
  const events = useDateProofEvents(loadedEvents);
  const fav = useFavorites();
  const [calView, setCalView] = useState<CalView>('month');
  const [currentDate, setCurrentDate] = useState<string>(() => eventTodayKey());
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  const today = eventTodayKey();

  // Map each event to its New York calendar-day key. All-day events are bucketed
  // by the *date portion* of their ISO timestamp so a 2026-04-27T00:00:00-04:00
  // event lands on April 27 regardless of the viewer's timezone (otherwise it
  // would render on April 26 for users west of EDT). Within each day we sort
  // all-day events to the top, then by start time.
  const eventsByDay = useMemo(() => {
    const map = new Map<string, ExtractedEvent[]>();
    for (const event of events) {
      const key = eventDayKey(event);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(event);
    }
    for (const list of map.values()) {
      list.sort(compareEventDates);
    }
    return map;
  }, [events]);

  // Assign a stable colour to each category
  const catColor = useMemo(() => {
    const cats = Array.from(new Set(events.map((e) => e.category).filter(Boolean) as string[]));
    const m = new Map<string, string>();
    cats.forEach((c, i) => m.set(c, CAT_PALETTE[i % CAT_PALETTE.length]));
    return m;
  }, [events]);

  const pillStyle = (event: ExtractedEvent): React.CSSProperties => ({
    '--cat-color': catColor.get(event.category ?? '') ?? 'var(--mh-primary)',
  } as React.CSSProperties);

  // ── shared navigation header ────────────────────────────────────────────────

  function NavHeader({ label, onPrev, onNext }: { label: string; onPrev: () => void; onNext: () => void }) {
    return (
      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <div className="btn-group">
          <button
            type="button"
            className={`btn btn-sm ${calView === 'month' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => { setCalView('month'); setExpandedDay(null); }}
          >
            Month
          </button>
          <button
            type="button"
            className={`btn btn-sm ${calView === 'week' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setCalView('week')}
          >
            Week
          </button>
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onPrev} aria-label="Previous">
          <i className="bi bi-chevron-left" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => { setCurrentDate(eventTodayKey()); setExpandedDay(null); }}
        >
          Today
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onNext} aria-label="Next">
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>
        <h2 className="h5 mb-0 ms-1">{label}</h2>
      </div>
    );
  }

  // ── month view ──────────────────────────────────────────────────────────────

  if (calView === 'month') {
    const month = currentDate.slice(0, 7);
    const firstOfMonth = `${month}-01`;
    const gridStart = addCalendarDays(firstOfMonth, -calendarWeekday(firstOfMonth)!)!;
    const days = Array.from({ length: 42 }, (_, i) => addCalendarDays(gridStart, i)!);
    const monthLabel = calendarLabel(currentDate, { month: 'long', year: 'numeric' });

    const prevMonth = () => {
      setCurrentDate((date) => addCalendarMonths(date, -1) ?? date);
      setExpandedDay(null);
    };
    const nextMonth = () => {
      setCurrentDate((date) => addCalendarMonths(date, 1) ?? date);
      setExpandedDay(null);
    };

    const MAX_VISIBLE = 3;

    return (
      <div className="mh-cal-month">
        <NavHeader label={monthLabel} onPrev={prevMonth} onNext={nextMonth} />

        <div className="overflow-auto" tabIndex={0} aria-label="Month calendar. Scroll horizontally to see every day on small screens.">
        <div className="mh-cal-grid mh-cal-grid-header" style={{ minWidth: '63rem' }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="mh-cal-col-header">{d}</div>
          ))}
        </div>

        <div className="mh-cal-grid mh-cal-grid-body" style={{ minWidth: '63rem' }}>
          {days.map((day, i) => {
            const key = day;
            const dayEvents = eventsByDay.get(key) ?? [];
            const isToday = day === today;
            const isThisMonth = day.slice(0, 7) === month;
            const isExpanded = expandedDay === key;
            const overflow = dayEvents.length - MAX_VISIBLE;

            return (
              <div
                key={i}
                className={[
                  'mh-cal-day',
                  isThisMonth ? '' : 'mh-cal-day-other',
                  isToday ? 'mh-cal-day-today' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="mh-cal-day-num">
                  <button
                    type="button"
                    className={`mh-cal-day-num-btn${isToday ? ' mh-cal-today-badge' : ''}`}
                    onClick={() => setExpandedDay(isExpanded ? null : key)}
                    aria-pressed={isExpanded}
                    aria-label={`${calendarLabel(day, { month: 'long', day: 'numeric' })}, ${dayEvents.length} event${dayEvents.length !== 1 ? 's' : ''}`}
                  >
                    {Number(day.slice(8, 10))}
                  </button>
                </div>

                <div className="mh-cal-events">
                  {(isExpanded ? dayEvents : dayEvents.slice(0, MAX_VISIBLE)).map((event) => {
                    const where = event.venueName ?? event.locationText;
                    const allDay = isAllDayEvent(event);
                    const timeLabel = timePillParts(event).start;
                    const favReason = isEventHighlighted(event, fav) ? eventFavoriteReason(event, fav) : null;
                    const favClass = favReason === 'event' ? ' mh-favorite' : favReason ? ' mh-favorite-inherited' : '';
                    return (
                    <div
                      key={event.rowKey}
                      className={`mh-cal-pill${allDay ? ' mh-cal-pill-allday' : ''}${favClass}`}
                      style={pillStyle(event)}
                      title={`${event.title} · ${formatRange(event, dateProofRangeOptions(event))}${where ? ` · ${where}` : ''}`}
                    >
                      <div className="d-flex flex-column align-items-start gap-1">
                        <span className="mh-cal-pill-time small">{timeLabel}</span>
                        <DateProofWarning event={event} />
                      </div>
                      <button
                        type="button"
                        className="mh-cal-pill-btn"
                        onClick={(ev) => { ev.stopPropagation(); onSelectEvent(event); }}
                      >
                        <span className="mh-cal-pill-dot" aria-hidden="true" />
                        <span className="mh-cal-pill-label">
                          {favReason && <span aria-hidden="true" className="mh-favorite-star is-on me-1">★</span>}
                          <EventReviewWarningChips warnings={nonTemporalWarnings(event.reviewWarnings)} compact className="me-1" />
                          {event.title}
                        </span>
                      </button>
                      {event.dateFlaggedWrong && (
                        <span className="badge mh-wrong-date-chip align-self-start">
                          <i className="bi bi-calendar-x me-1" aria-hidden="true" />Wrong date
                        </span>
                      )}
                      {where && (
                        <span className="mh-cal-pill-venue">
                          {event.venueId ? (
                            <Link
                              to={`/venues/${event.venueId}`}
                              onClick={(ev) => ev.stopPropagation()}
                              aria-label={`See upcoming events at ${where}`}
                            >
                              {where}
                            </Link>
                          ) : (
                            <span className="text-muted">{where}</span>
                          )}
                        </span>
                      )}
                    </div>
                    );
                  })}

                  {!isExpanded && overflow > 0 && (
                    <button
                      type="button"
                      className="mh-cal-more-btn"
                      onClick={() => setExpandedDay(key)}
                    >
                      +{overflow} more
                    </button>
                  )}

                  {isExpanded && dayEvents.length > MAX_VISIBLE && (
                    <button
                      type="button"
                      className="mh-cal-more-btn"
                      onClick={() => setExpandedDay(null)}
                    >
                      Show less
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>

        {events.length === 0 && (
          <p className="text-muted small text-center mt-3">
            No events in the current filter window. Try changing the <strong>When</strong> filter above.
          </p>
        )}
      </div>
    );
  }

  // ── week view ───────────────────────────────────────────────────────────────

  const weekStart = addCalendarDays(currentDate, -calendarWeekday(currentDate)!)!;
  const weekDays = Array.from({ length: 7 }, (_, i) => addCalendarDays(weekStart, i)!);

  const weekLabel = `${calendarLabel(weekDays[0], { month: 'short', day: 'numeric' })} – ${calendarLabel(weekDays[6], { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const prevWeek = () => setCurrentDate((date) => addCalendarDays(date, -7) ?? date);
  const nextWeek = () => setCurrentDate((date) => addCalendarDays(date, 7) ?? date);

  return (
    <div className="mh-cal-week">
      <NavHeader label={weekLabel} onPrev={prevWeek} onNext={nextWeek} />

      <div className="overflow-auto" tabIndex={0} aria-label="Week calendar. Scroll horizontally to see every day on small screens.">
      <div className="mh-cal-week-grid" style={{ minWidth: '63rem' }}>
        {weekDays.map((day, i) => {
          const key = day;
          const dayEvents = eventsByDay.get(key) ?? [];
          const isToday = day === today;

          return (
            <div
              key={i}
              className={`mh-cal-week-day${isToday ? ' mh-cal-week-day-today' : ''}`}
            >
              <div className="mh-cal-week-day-header">
                <span className="mh-cal-week-dow">
                  {calendarLabel(day, { weekday: 'short' })}
                </span>
                <span className={`mh-cal-week-date${isToday ? ' mh-cal-today-badge' : ''}`}>
                  {Number(day.slice(8, 10))}
                </span>
              </div>

              <div className="mh-cal-week-events">
                {dayEvents.length === 0 ? (
                  <p className="text-muted small text-center py-2 mb-0">—</p>
                ) : (
                  dayEvents.map((event) => {
                    const favReason = isEventHighlighted(event, fav) ? eventFavoriteReason(event, fav) : null;
                    const favClass = favReason === 'event' ? ' mh-favorite' : favReason ? ' mh-favorite-inherited' : '';
                    return (
                    <div
                      key={event.rowKey}
                      className={`mh-cal-week-event${isAllDayEvent(event) ? ' mh-cal-week-event-allday' : ''}${favClass}`}
                      style={pillStyle(event)}
                    >
                      <div className="d-flex flex-column align-items-start gap-1">
                        <span className="text-muted mh-cal-week-event-meta">{timePillParts(event).start}</span>
                        <DateProofWarning event={event} />
                      </div>
                      <button
                        type="button"
                        className="mh-cal-week-event-btn"
                        onClick={() => onSelectEvent(event)}
                        title={formatRange(event, dateProofRangeOptions(event))}
                      >
                        <span className="d-block fw-semibold small lh-sm">
                          {favReason && <span aria-hidden="true" className="mh-favorite-star is-on me-1">★</span>}
                          {event.recurringPattern && event.recurringPattern !== 'one-time' && (
                            <span
                              aria-label={event.recurringIsPermanent
                                ? `Ongoing series, ${formatTag(event.recurringPattern)}`
                                : `Recurring ${formatTag(event.recurringPattern)}`}
                              title={
                                event.recurringIsPermanent
                                  ? `Ongoing ${formatTag(event.recurringPattern)} series${event.recurringDaysOfWeek?.length ? ` on ${event.recurringDaysOfWeek.join(', ')}` : ''}`
                                  : event.recurringDaysOfWeek?.length
                                    ? `Recurs ${formatTag(event.recurringPattern)} on ${event.recurringDaysOfWeek.join(', ')}`
                                    : `Recurs ${formatTag(event.recurringPattern)}`
                              }
                              className="me-1"
                            >
                              {event.recurringIsPermanent ? '♾️' : '🔁'}
                            </span>
                          )}
                          {isStaleRecurringProjection(event) && (
                            <span
                              aria-label="Projected occurrence — not refreshed by a recent post"
                              title="Projected from a recurring series — no newer post has confirmed this specific date."
                              className="me-1 text-muted"
                            >
                              📭
                            </span>
                          )}
                          <EventReviewWarningChips warnings={nonTemporalWarnings(event.reviewWarnings)} compact className="me-1" />
                          {event.title}
                        </span>
                      </button>
                      {event.dateFlaggedWrong && (
                        <span className="badge mh-wrong-date-chip">
                          <i className="bi bi-calendar-x me-1" aria-hidden="true" />Wrong date
                        </span>
                      )}
                      {(event.venueName || event.locationText) && (
                        <div className="mh-cal-week-event-venue small">
                          {event.venueId ? (
                            <Link
                              to={`/venues/${event.venueId}`}
                              aria-label={`See upcoming events at ${event.venueName ?? event.locationText}`}
                            >
                              {event.venueName ?? event.locationText}
                            </Link>
                          ) : (
                            <span className="text-muted">{event.venueName ?? event.locationText}</span>
                          )}
                        </div>
                      )}
                      {event.category && (
                        <span className="badge text-bg-light border mt-1" style={{ fontSize: '0.65rem' }}>
                          {formatTag(event.category)}
                        </span>
                      )}
                    </div>
                  );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
      </div>

      {events.length === 0 && (
        <p className="text-muted small text-center mt-3">
          No events in the current filter window. Try changing the <strong>When</strong> filter above.
        </p>
      )}
    </div>
  );
}
